import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download, BellRing, Loader2, Coins, FileDigit, FileCheck, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr, downloadFile, apiError } from "@/lib/api";
import StatutBadge from "@/components/StatutBadge";
import Modal from "@/components/Modal";
import PhotoCapture from "@/components/PhotoCapture";

export default function FactureDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [facture, setFacture] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [relanceOpen, setRelanceOpen] = useState(false);
  const [envoiOpen, setEnvoiOpen] = useState(false);
  const [validerOpen, setValiderOpen] = useState(false);
  const [supprOpen, setSupprOpen] = useState(false);
  const [canal, setCanal] = useState("email");
  const [payForm, setPayForm] = useState({ montant: "", moyen: "virement" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/factures/${id}`).then((r) => {
    setFacture(r.data);
    setPayForm((p) => ({ ...p, montant: String(Math.max(0, r.data.montant_ttc - (r.data.montant_paye || 0)).toFixed(2)) }));
  }).catch(() => navigate("/devis"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!facture) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;

  const reste = Math.max(0, facture.montant_ttc - (facture.montant_paye || 0));

  async function payer() {
    setBusy(true);
    try {
      await api.post(`/factures/${id}/paiements`, { montant: Number(payForm.montant) || 0, moyen: payForm.moyen });
      toast.success(t("facture.paye_toast"));
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function relancer() {
    setBusy(true);
    try {
      await api.post(`/factures/${id}/relance`, { canal });
      toast.success(t("facture.relance_toast"));
      setRelanceOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function envoyer() {
    setBusy(true);
    try {
      await api.post(`/factures/${id}/envoyer`);
      toast.success(t("facture.envoye_toast"));
      setEnvoiOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function valider() {
    setBusy(true);
    try {
      await api.post(`/factures/${id}/valider`);
      toast.success(t("facture.validee_toast", { numero: facture.numero }));
      setValiderOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function supprimer() {
    setBusy(true);
    try {
      await api.delete(`/factures/${id}`);
      toast.success(t("facture.supprime", { numero: facture.numero }));
      navigate("/devis");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
      setSupprOpen(false);
    }
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-facture-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/devis")} className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1]"
                data-testid="btn-retour">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("dash.facture")} {facture.numero}</h1>
          <p className="text-stone-500 text-sm">
            {facture.client?.nom}
            {facture.statut === "brouillon"
              ? ` · ${t("facture.brouillon_non_valide")}`
              : facture.statut === "a_envoyer"
                ? ` · ${t("facture.a_envoyer_hint")}`
              : <> · {t("facture.echeance")} {dateFr(facture.echeance)}</>}
            {facture.validee_le && <> · {t("facture.validee_le")} {dateFr(facture.validee_le)}</>}
            {facture.devis && <> · <Link to={`/devis/${facture.devis.id}`} className="text-[#8A5A2B] font-semibold">{t("facture.devis_num", { numero: facture.devis.numero })}</Link></>}
          </p>
        </div>
        <StatutBadge statut={facture.statut} envoyeeLe={facture.envoyee_le} testid="badge-statut-facture" />
      </div>

      <div className="bg-[#12100D] rounded-xl p-5 border border-[#D9A461]/30" data-testid="bloc-montant-facture">
        <div className="flex justify-between text-stone-400 text-sm mb-1">
          <span>{t("devis_form.total_ht")}</span><span className="font-serif">{eur(facture.montant_ht)}</span>
        </div>
        {(facture.ventilation_tva || [{ taux: facture.tva, base_ht: facture.montant_ht, montant_tva: facture.montant_ttc - facture.montant_ht, autoliquidation: false }]).map((v) => (
          <div key={v.autoliquidation ? "AL" : v.taux} className="flex justify-between text-stone-400 text-sm mb-1"
               data-testid={`facture-tva-${v.autoliquidation ? "al" : String(v.taux).replace(".", "-")}`}>
            <span>{v.autoliquidation ? t("devis_form.al_zero") : t("devis_form.tva_ligne", { taux: String(v.taux).replace(".", ","), base: eur(v.base_ht) })}</span>
            <span className="font-serif">{eur(v.montant_tva)}</span>
          </div>
        ))}
        {facture.autoliquidation && (
          <p className="text-[#D9A461] text-xs mb-2 font-semibold" data-testid="mention-autoliquidation">
            {t("devis_form.al_mention")}
          </p>
        )}
        <div className="flex justify-between items-center pt-2 border-t border-white/10">
          <span className="text-white font-semibold">{t("facture.total_ttc")}</span>
          <span className="amount-display">{eur(facture.montant_ttc)}</span>
        </div>
        <div className="flex justify-between text-stone-400 text-sm mt-2">
          <span>{t("facture.deja_encaisse")}</span><span className="font-serif">{eur(facture.montant_paye || 0)}</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-stone-400">{t("facture.reste")}</span>
          <span className={`font-serif font-bold ${reste > 0 ? "text-[#D9A461]" : "text-[#7BC496]"}`}>{eur(reste)}</span>
        </div>
      </div>

      <div className="panel !p-0 overflow-hidden">
        <div className="divide-y divide-[#E6DFD1]">
          {facture.lignes.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 p-4 text-sm">
              <div>
                <div className="font-medium">{l.designation}</div>
                <div className="text-stone-500 text-xs">{l.quantite}{l.unite ? ` ${l.unite}` : ""} × {eur(l.prix_unitaire)}</div>
              </div>
              <span className="font-serif font-semibold shrink-0">{eur(l.quantite * l.prix_unitaire)}</span>
            </div>
          ))}
          {((facture.mode_mo === "horaire" && Number(facture.heures_mo) > 0)
            || (facture.mode_mo !== "horaire" && Number(facture.montant_forfait_mo) > 0)) && (
            <div className="flex justify-between gap-3 p-4 text-sm" data-testid="ligne-main-oeuvre">
              <div>
                <div className="font-medium">{t("devis_form.main_oeuvre")}</div>
                <div className="text-stone-500 text-xs">{facture.mode_mo === "horaire"
                  ? `${facture.heures_mo} h × ${eur(facture.taux_horaire_mo)}/h`
                  : t("devis_form.mode_forfait")}</div>
              </div>
              <span className="font-serif font-semibold shrink-0">{eur(facture.mode_mo === "horaire"
                ? (facture.heures_mo || 0) * (facture.taux_horaire_mo || 0)
                : (facture.montant_forfait_mo || 0))}</span>
            </div>
          )}
        </div>
      </div>

      {facture.paiements.length > 0 && (
        <div className="panel">
          <h2 className="font-serif font-semibold text-lg mb-3">{t("facture.paiements")}</h2>
          <div className="divide-y divide-[#E6DFD1]">
            {facture.paiements.map((p) => (
              <div key={p.id} className="flex justify-between py-2 text-sm">
                <span>{dateFr(p.date)} · {t(`facture.${p.moyen}`, p.moyen)}</span>
                <span className="font-serif font-semibold text-[#276440]">{eur(p.montant)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {facture.relances?.length > 0 && (
        <div className="panel">
          <h2 className="font-serif font-semibold text-lg mb-3">{t("facture.relances")}</h2>
          <div className="divide-y divide-[#E6DFD1]">
            {facture.relances.map((r, i) => (
              <div key={i} className="flex justify-between py-2 text-sm">
                <span>{dateFr(r.date)} · {r.canal}{r.auto ? ` · ${t("commun.automatique")}` : ""}</span>
                <span className="text-stone-500">J+{r.jours_retard}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {facture.statut !== "payee" && (
          <button onClick={() => setEnvoiOpen(true)} className="btn-accent" data-testid="btn-envoyer-facture">
            <Send size={18} /> {t("facture.envoyer")}
          </button>
        )}
        {["brouillon", "a_envoyer"].includes(facture.statut) && (
          <button onClick={() => setValiderOpen(true)} className="btn-accent" data-testid="btn-valider-facture">
            <FileCheck size={18} /> {t("facture.valider")}
          </button>
        )}
        {["brouillon", "a_envoyer"].includes(facture.statut) && (
          <button onClick={() => setSupprOpen(true)} className="btn-secondary !text-[#A32E20]"
                  data-testid="btn-supprimer-facture">
            <Trash2 size={18} /> {t("facture.supprimer")}
          </button>
        )}
        <button onClick={() => downloadFile(`/factures/${id}/pdf`, `${facture.numero}.pdf`)} className="btn-secondary"
                data-testid="btn-telecharger-pdf-facture">
          <Download size={18} /> {["brouillon", "a_envoyer"].includes(facture.statut) ? t("facture.pdf_brouillon") : t("facture.pdf")}
        </button>
        {!["brouillon", "a_envoyer"].includes(facture.statut) && (
          <button onClick={() => downloadFile(`/factures/${id}/facturx`, `${facture.numero}-facturx.pdf`)}
                  className="btn-secondary" data-testid="btn-facturx"
                  title={t("facture.facturx_title")}>
            <FileDigit size={18} /> {t("facture.facturx")}
          </button>
        )}
        {!["payee", "brouillon", "a_envoyer"].includes(facture.statut) && (
          <>
            <button onClick={() => setPayOpen(true)} className="btn-accent" data-testid="btn-enregistrer-paiement">
              <Coins size={18} /> {t("facture.paiement")}
            </button>
            <button onClick={() => setRelanceOpen(true)} className="btn-secondary" data-testid="btn-relancer">
              <BellRing size={18} /> {t("facture.relancer")}
            </button>
          </>
        )}
        {facture.statut !== "payee" && (
          <PhotoCapture factureId={id} clientId={facture.client_id} type="facture_fournisseur"
                        className="btn-secondary" testid="btn-photo-justificatif">
            {t("facture.photo_justif")}
          </PhotoCapture>
        )}
      </div>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={t("facture.paiement_titre")} testid="modal-paiement">
        <label className="block text-sm font-medium mb-1.5">{t("facture.montant")}</label>
        <input className="input-field mb-4" type="number" min="0" step="0.01" value={payForm.montant}
               onChange={(e) => setPayForm({ ...payForm, montant: e.target.value })} data-testid="input-montant-paiement" />
        <label className="block text-sm font-medium mb-1.5">{t("facture.moyen")}</label>
        <select className="input-field mb-5" value={payForm.moyen}
                onChange={(e) => setPayForm({ ...payForm, moyen: e.target.value })} data-testid="select-moyen-paiement">
          <option value="virement">{t("facture.virement")}</option>
          <option value="cheque">{t("facture.cheque")}</option>
          <option value="especes">{t("facture.especes")}</option>
          <option value="carte">{t("facture.carte")}</option>
        </select>
        <button onClick={payer} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-paiement">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("facture.valider_paiement")}
        </button>
      </Modal>

      <Modal open={validerOpen} onClose={() => setValiderOpen(false)} title={t("facture.valider_titre")} testid="modal-valider-facture">
        <p className="text-sm text-stone-600 mb-5">{t("facture.valider_texte", { numero: facture.numero })}</p>
        <button onClick={valider} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-validation">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <FileCheck size={18} />} {t("facture.valider_confirmer")}
        </button>
      </Modal>

      <Modal open={envoiOpen} onClose={() => setEnvoiOpen(false)} title={t("facture.envoi_titre")} testid="modal-envoyer-facture">
        <p className="text-sm text-stone-600 mb-1">{t("facture.envoi_texte", { numero: facture.numero })}</p>
        <p className="text-sm font-medium mb-5" data-testid="envoi-email-client">{facture.client?.email || t("facture.envoi_sans_email")}</p>
        <button onClick={envoyer} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-envoi">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {t("facture.envoi_confirmer")}
        </button>
      </Modal>

      <Modal open={relanceOpen} onClose={() => setRelanceOpen(false)} title={t("facture.relance_titre")} testid="modal-relance">
        <p className="text-sm text-stone-600 mb-4">{t("facture.relance_texte")}</p>
        <div className="grid grid-cols-1 gap-2 mb-5">
          {[["email", "Email"]].map(([v, l]) => (
            <button key={v} onClick={() => setCanal(v)} data-testid={`relance-canal-${v}`}
                    className={`h-12 rounded-lg font-semibold text-sm border ${
                      canal === v ? "bg-[#12100D] text-[#D9A461] border-[#D9A461]/40" : "bg-white border-[#E6DFD1] text-stone-600"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={relancer} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-relance">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <BellRing size={18} />} {t("facture.envoyer_relance")}
        </button>
      </Modal>

      <Modal open={supprOpen} onClose={() => setSupprOpen(false)} title={t("facture.supprimer_titre")} testid="modal-supprimer-facture">
        <p className="text-sm text-stone-600 mb-5">{t("facture.supprimer_texte", { numero: facture.numero })}</p>
        <button onClick={supprimer} disabled={busy} className="btn-accent w-full !bg-[#A32E20]" data-testid="btn-confirmer-suppression-facture">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />} {t("facture.supprimer_confirmer")}
        </button>
      </Modal>
    </div>
  );
}
